import React from "react";
import { getPostByIdOrSlug } from "server/blog-client";

import { baseTemplates } from "templates/src/data";
import { BaseTemplate } from "templates/src/types";
import { PageHeadTags } from "common/head-tags";
import {
  serverRoutes,
  templateServerRouteBySlug,
} from "client-server-shared/config/routes";
import { MainBackgroundWrapper } from "modules/non-protected-page/shared";
import { colors } from "src/constants";
import {
  Container,
  ResponsiveText,
} from "modules/marketing-pages/front-page/shared";
import {
  SelectedTemplateContextProvider,
  useGetTemplate,
  useSelectedTemplateContext,
} from "context/selected-template-context";
import TemplateFormRenderer from "modules/template-by-id/template-form-renderer";
import { PurpleButton } from "components/purple-button";
import { Box } from "components/ui/box";
import { Section } from "modules/selections/common";
import { LanguageSelectionPlain } from "modules/selections/language-selection-plain";
import { MarkdownRenderer } from "components/markdown";
import FaqList from "modules/non-protected-page/faq-list";
import Script from "next/script";
import { TooManyRequestListener } from "components/too-many-request";
import { buildJsonLd } from "client-server-shared/utils/faq";
import { PopupContextProvider, useOpenPopup } from "context/popup-context";
import { useNotifications } from "hooks/use-notification";
import { getReadableStreamErrorWithFetch } from "client-server-shared/utils/error-parsing";
import { ErrorCode } from "server/errors/utils";
import { useFetchUserDetailsDataOnlyOnce } from "hooks/user-user-details-data";
import { useUser } from "hooks/use-user";
import { onOtherLimitReached } from "utils/other-limit";
import { LimitReachedType } from "client-server-shared/types/limit";
import { useStream } from "modules/collection-by-id/editor/use-stream";
import freeToolsApi from "utils/free-tools-api";
import { Paper } from "components/ui/paper";
import { Description, Typography } from "components/ui/Typography";
import { countWordsPlain } from "client-server-shared/utils/countWords";
import CopyButtonWithOptions from "components/copy-button-with-options";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { locale } from "client-server-shared/constants";
const ContentRenderer = () => {
  const { template } = useSelectedTemplateContext();
  return (
    <Container
      maxWidth={"lg"}
      component="article"
      sx={{
        padding: 0,
        flexBasis: "100%",
      }}
    >
      <Box
        sx={{
          border: `1px solid ${colors.gray2}`,
          borderRadius: "10px",
          padding: "48px",
        }}
      >
        <MarkdownRenderer
          input={template.content}
          className="markdown-v3 blog-markdown"
        />
      </Box>
    </Container>
  );
};

const Results = ({ output, loading }: { output: string }) => {
  return (
    <Box
      sx={{
        border: `1px solid ${colors.gray2}`,
        borderRadius: "10px",
        padding: "48px 24px",
        marginTop: "48px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 500,
            textAlign: "left",
          }}
        >
          Results
        </Typography>
        {output && (
          <Box>
            <Description>
              {output.length} characters · {countWordsPlain(output)} words
            </Description>
          </Box>
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          rowGap: "24px",
          padding: "24px 0",
        }}
      >
        {!output && !loading.isLoading && (
          <Box>
            <Typography
              variant="body2"
              sx={{
                marginTop: "24px",
                textAlign: "center",
                color: "rgb(99, 115, 129)",
              }}
            >
              Your generated content will appear here
            </Typography>
          </Box>
        )}
        <Box
          sx={{
            display: "flex",
            textAlign: "left",
          }}
        >
          <MarkdownRenderer
            input={output || ""}
            className="markdown-v3 blog-markdown"
          />
        </Box>
        {!loading.isLoading && output && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <CopyButtonWithOptions
              content={output}
              getPlainText={() => {
                return output;
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

const ToolPage = () => {
  const template = useGetTemplate();
  const { getValue, onValueChange, canGenerate, valuesToApiFormat } =
    useSelectedTemplateContext();
  const onOpenPopup = useOpenPopup();
  const user = useUser();
  const { addFailureNotification } = useNotifications();
  const [output, setOutput] = React.useState("");

  const handleError = async (response: Response) => {
    const errorJson = await getReadableStreamErrorWithFetch(response);
    if (errorJson?.code === ErrorCode.FreeLimitReached) {
      if (!user) {
        onOpenPopup("limitReached", {
          title: `Login to continue using this tool`,
          description1:
            "It seems like you've enjoyed our free tools! To keep the fun going, we invite you to log in.",
          actionText: "Login",
          action: () => {
            window.open(serverRoutes.register, "_blank");
          },
        });
      } else {
        onOtherLimitReached(LimitReachedType.requiredPaidPlan, {
          title: "Upgrade to continue",
          actionText: "Upgrade",
          description1:
            "To continue using this feature, you'll need to be on a paid plan.",
        });
      }
    } else if (errorJson?.code !== ErrorCode.RateLimitError) {
      addFailureNotification(
        "There's an error when generating content, please try again."
      );
    }
  };

  const { initStream, streaming, loading } = useStream({
    setResult: (result) => setOutput(result.content),
    onHandleError: handleError,
    shouldFixMarkdown: true,
  });
  const isLoading = streaming.isLoading || loading.isLoading;

  const onGenerate = async () => {
    if (!canGenerate) {
      return;
    }
    if (isLoading) {
      return;
    }
    const request = freeToolsApi.onFreeToolGenerate({
      data: valuesToApiFormat,
    });
    setOutput("");
    await initStream(() => request);
  };

  return (
    <>
      <PageHeadTags
        pageLink={templateServerRouteBySlug(template.slug)}
        title={template.metaTitle}
        description={template.metaDescription}
      />
      <MainBackgroundWrapper
        component="section"
        sx={{
          background: colors.white1,
          textAlign: "center",
          padding: "124px 0 24px 0",
        }}
      >
        <ResponsiveText
          variant="h1"
          fontSize={50}
          sx={{
            position: "relative",
            maxWidth: "500px",
            margin: "auto",
            color: colors.black1,
          }}
        >
          {template.name}
        </ResponsiveText>
        <ResponsiveText
          fontSize={20}
          sx={{
            color: colors.gray1,
            marginTop: "24px",
          }}
        >
          {template.shortDescription}
        </ResponsiveText>
      </MainBackgroundWrapper>
      <Container
        component="section"
        maxWidth="md"
        sx={{
          margin: "36px auto 124px auto",
        }}
      >
        <Box
          sx={{
            border: `1px solid ${colors.gray2}`,
            borderRadius: "10px",
            padding: "48px 24px",
          }}
        >
          <TemplateFormRenderer template={template} />
          <Section
            sx={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <LanguageSelectionPlain
              optional={true}
              required={false}
              title="Language"
              info="Select the language you want to generate content in."
              onChange={(e) => {
                onValueChange(e.target.value, "language");
              }}
              value={getValue("language")}
              selectProps={{
                sx: {
                  "&.MuiInputBase-root": {
                    marginRight: "auto",
                  },
                },
              }}
            />
          </Section>
          <PurpleButton
            disabled={!canGenerate}
            onClick={() => {
              onGenerate();
            }}
            loading={isLoading}
            capitalize
            sx={{
              fontSize: "16px",
              width: "100%",
            }}
          >
            Generate
          </PurpleButton>
        </Box>
        <Results output={output} loading={isLoading} />
      </Container>
      <ContentRenderer />
      <FaqList faqLists={template.faqs} />
      <TooManyRequestListener />
      {template.faqs && (
        <Script
          id={`${template.slug}-faq-json-ld`}
          key={`${template.slug}-faq-json-ld`}
          strategy="beforeInteractive"
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(buildJsonLd(template.faqs)),
          }}
        />
      )}
    </>
  );
};

const ToolPageContainer = ({ template }: { template: BaseTemplate }) => {
  useFetchUserDetailsDataOnlyOnce();

  return (
    <PopupContextProvider>
      <SelectedTemplateContextProvider template={template}>
        <ToolPage />
      </SelectedTemplateContextProvider>
    </PopupContextProvider>
  );
};

export async function getStaticProps({ params }) {
  const template = baseTemplates.find(
    (template) => template.slug === params.slug
  );
  if (!template) {
    return {
      notFound: true,
    };
  }

  const templatePost = await getPostByIdOrSlug(template.slug);
  const { buildSystemPrompt, buildUserPrompt, ...rest } = template;

  if (!templatePost) {
    return {
      props: {
        ...(await serverSideTranslations(locale, ["common"])),
        template: {
          ...rest,
          content: "",
          metaTitle: rest.name,
          metaDescription: rest.shortDescription,
          faqs: [],
        },
      },
      revalidate: 3600,
    };
  }

  return {
    props: {
      ...(await serverSideTranslations(locale, ["common"])),
      template: {
        ...rest,
        content: templatePost.markdown,
        metaTitle: templatePost.metaTitle,
        metaDescription: templatePost.metaDescription,
        faqs: templatePost.faqs || [],
      },
    },
    revalidate: 3600,
  };
}

export async function getStaticPaths() {
  const paths = baseTemplates
    .filter((template) => template.slug !== "ai-article-writer")
    .map((template) => ({
      params: {
        slug: template.slug,
      },
    }));

  return {
    paths,
    fallback: "blocking",
  };
}

export default ToolPageContainer;
